/**
 * Profanity filter utility to detect and block foul language
 * Includes common words, leet-speak variants, and slurs
 */

// Whitelist words that contain profanity substrings but are acceptable
const WHITELIST = new Set([
  'scunthorpe',
  'penistone',
  'shitake',
  'shiitake',
  'cockpit',
  'cocktail',
  'cockatoo',
  'cockatiel',
  'peacock',
  'hancock',
  'hitchcock',
  'woodcock',
  'shuttlecock',
  'assassin',
  'assassinate',
  'assassination',
  'class',
  'classic',
  'classy',
  'bass',
  'bassist',
  'grass',
  'brass',
  'mass',
  'massive',
  'pass',
  'passing',
  'compass',
  'embarrass',
  'embarrassed',
  'harass',
  'harassment',
  'assume',
  'assumption',
  'assist',
  'assistant',
  'assistance',
  'cassette',
  'asset',
  'assess',
  'assessment',
  'basement',
  'cass',
  'cassidy',
  'cassandra',
  'dick', // Can be a name
  'dickens',
  'dickenson',
  'dickinson',
  'spotted',
  'spotter',
  'spit',
  'split',
  'dispute',
  'disputing',
  'compute',
  'computer',
  'computing',
  'repute',
  'reputation',
  'execute',
  'execution',
  'prosecutor',
  'persecution',
  'butcher',
  'butter',
  'button',
  'buttress',
  'butterfly',
  'butterscotch',
  'peanut butter',
  'rebuttal',
  'snigger', // British English for snicker
  'niggardly', // Archaic word meaning miserly (no relation to slur)
  'vinegar',
  'trigger',
  'bigger',
  'digger',
  'jigger',
  'rigger',
  'analyze',
  'analysis',
  'analyst',
  'canal',
  'analytical',
  'therapist',
  'therapeutic',
  'grape',
  'scrape',
  'drape',
  'drapery',
  'trappist',
  'whopper',
  'wrapper',
  'horoscope',
  'microscope',
  'telescope',
  'arsenal',
  'parse',
  'arsehole', // Will be caught by exact match, but "arsenal" won't
]);

// Base profanity words (will be matched with variations)
const PROFANITY_BASE_WORDS = [
  // Common profanity
  'fuck',
  'shit',
  'ass',
  'bitch',
  'bastard',
  'damn',
  'cunt',
  'cock',
  'dick',
  'penis',
  'vagina',
  'pussy',
  'tits',
  'boobs',
  'whore',
  'slut',
  'piss',
  'crap',
  'fag',
  'faggot',
  'douche',
  'wanker',
  'twat',
  'prick',
  'bollocks',
  'arse',
  'arsehole',
  'asshole',
  'motherfucker',
  'bullshit',
  'horseshit',
  'jackass',
  'dumbass',
  'smartass',
  'badass',
  'dipshit',
  'douchebag',
  'fuckface',
  'shithead',
  'dickhead',
  'cocksucker',
  'asswipe',
  'butthead',
  'buttface',
  'fuckwit',
  'numbnuts',
  'nutjob',
  
  // Slurs and hate speech
  'nigger',
  'nigga',
  'chink',
  'spic',
  'wetback',
  'beaner',
  'kike',
  'gook',
  'jap',
  'cracker',
  'honky',
  'gringo',
  'tranny',
  'shemale',
  'retard',
  'retarded',
  'spaz',
  'spastic',
  'cripple',
  'midget',
  'dyke',
  'lesbo',
  'homo',
  'queer',
  
  // Sexual terms
  'blowjob',
  'handjob',
  'rimjob',
  'cumshot',
  'cum',
  'jizz',
  'semen',
  'ejaculate',
  'orgasm',
  'masturbate',
  'masturbation',
  'dildo',
  'vibrator',
  'anal',
  'anus',
  'butthole',
  'clitoris',
  'erection',
  'horny',
  'boner',
  'fellatio',
  'cunnilingus',
  'sodomy',
  'pornography',
  'porn',
  'xxx',
  'nsfw',
];

// Leet speak character substitutions
const LEET_MAP: Record<string, string[]> = {
  'a': ['a', '@', '4', '^', 'à', 'á', 'â', 'ã', 'ä'],
  'b': ['b', '8', '|3', 'ß'],
  'c': ['c', '(', '<', 'ç'],
  'd': ['d', '|)', 'đ'],
  'e': ['e', '3', '€', 'è', 'é', 'ê', 'ë'],
  'f': ['f', 'ph', '|='],
  'g': ['g', '9', '6'],
  'h': ['h', '#', '|-|'],
  'i': ['i', '1', '!', '|', 'ì', 'í', 'î', 'ï'],
  'j': ['j', ']'],
  'k': ['k', '|<', '|{'],
  'l': ['l', '1', '|', '£'],
  'm': ['m', '|\\/|', '/\\/\\'],
  'n': ['n', '|\\|', 'ñ'],
  'o': ['o', '0', '()', 'ò', 'ó', 'ô', 'õ', 'ö', 'ø'],
  'p': ['p', '|*', '|>'],
  'q': ['q', '9', '(,)'],
  'r': ['r', '|2', '®'],
  's': ['s', '$', '5', 'š'],
  't': ['t', '7', '+'],
  'u': ['u', '|_|', 'ù', 'ú', 'û', 'ü'],
  'v': ['v', '\\/', '\\/'],
  'w': ['w', '\\/\\/', 'vv'],
  'x': ['x', '><', '×'],
  'y': ['y', '¥', 'ÿ'],
  'z': ['z', '2', 'ž'],
};

/**
 * Normalize text by removing common leet-speak substitutions
 */
function normalizeLeetSpeak(text: string): string {
  let normalized = text.toLowerCase();
  
  // Replace leet characters with their letter equivalents
  for (const [letter, substitutes] of Object.entries(LEET_MAP)) {
    for (const sub of substitutes) {
      if (sub !== letter && sub.length === 1) {
        normalized = normalized.split(sub).join(letter);
      }
    }
  }
  
  // Handle multi-character substitutes
  normalized = normalized
    .replace(/ph/g, 'f')
    .replace(/\|3/g, 'b')
    .replace(/\|\)/g, 'd')
    .replace(/\|=/g, 'f')
    .replace(/\|-\|/g, 'h')
    .replace(/\|</g, 'k')
    .replace(/\|\{/g, 'k')
    .replace(/\|\\?\|/g, 'm')
    .replace(/\|\\n/g, 'n')
    .replace(/\(\)/g, 'o')
    .replace(/\|\*/g, 'p')
    .replace(/\|>/g, 'p')
    .replace(/\|2/g, 'r')
    .replace(/\|_\|/g, 'u')
    .replace(/\\?\/?/g, 'v')
    .replace(/><|×/g, 'x');
  
  // Remove repeated characters beyond 2 (e.g., "fuuuuck" -> "fuuck")
  normalized = normalized.replace(/(.)\1{2,}/g, '$1$1');
  
  // Remove common separators between letters
  normalized = normalized.replace(/[.\-_*#~]/g, '');
  
  return normalized;
}

/**
 * Check if a word is in the whitelist
 */
function isWhitelisted(word: string): boolean {
  return WHITELIST.has(word.toLowerCase());
}

/**
 * Build regex patterns for profanity detection
 */
function buildProfanityPatterns(): RegExp[] {
  const patterns: RegExp[] = [];
  
  for (const word of PROFANITY_BASE_WORDS) {
    // Skip very short words that need exact matching
    if (word.length <= 3) {
      // For short words, require word boundaries
      patterns.push(new RegExp(`\\b${escapeRegex(word)}\\b`, 'i'));
    } else {
      // For longer words, allow some flexibility with leet speak
      // Build a pattern that matches the word with possible separators
      let pattern = '';
      for (let i = 0; i < word.length; i++) {
        const char = word[i];
        const leetVariants = LEET_MAP[char] || [char];
        const charPattern = leetVariants.map(escapeRegex).join('|');
        pattern += `(?:${charPattern})`;
        // Allow optional separators between characters (but not at the end)
        if (i < word.length - 1) {
          pattern += '[.\\-_*#~\\s]?';
        }
      }
      patterns.push(new RegExp(`\\b${pattern}\\b`, 'i'));
    }
  }
  
  return patterns;
}

/**
 * Escape special regex characters
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Pre-compiled patterns for performance
const PROFANITY_PATTERNS = buildProfanityPatterns();

/**
 * Check if text contains profanity
 * @param text - The text to check
 * @returns true if profanity is detected, false otherwise
 */
export function containsProfanity(text: string): boolean {
  if (!text || typeof text !== 'string') {
    return false;
  }
  
  // Normalize the text
  const normalized = normalizeLeetSpeak(text);
  const words = normalized.split(/\s+/);
  
  // Check each word against whitelist first
  const nonWhitelistedText = words
    .filter(word => !isWhitelisted(word))
    .join(' ');
  
  // Also check the original text in case whitelist didn't apply
  const originalNormalized = text.toLowerCase();
  const originalWords = originalNormalized.split(/\s+/);
  const originalNonWhitelisted = originalWords
    .filter(word => !isWhitelisted(word))
    .join(' ');
  
  // Check against all patterns
  for (const pattern of PROFANITY_PATTERNS) {
    if (pattern.test(nonWhitelistedText) || pattern.test(originalNonWhitelisted)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Get list of profanity matches found in text (for debugging)
 * @param text - The text to check
 * @returns Array of matched profane words/phrases
 */
export function getProfanityMatches(text: string): string[] {
  if (!text || typeof text !== 'string') {
    return [];
  }
  
  const matches: string[] = [];
  const normalized = normalizeLeetSpeak(text);
  const words = normalized.split(/\s+/);
  
  const nonWhitelistedText = words
    .filter(word => !isWhitelisted(word))
    .join(' ');
  
  for (let i = 0; i < PROFANITY_PATTERNS.length; i++) {
    const pattern = PROFANITY_PATTERNS[i];
    const match = nonWhitelistedText.match(pattern);
    if (match) {
      matches.push(PROFANITY_BASE_WORDS[i]);
    }
  }
  
  return [...new Set(matches)]; // Remove duplicates
}

/**
 * Sanitize text by replacing profanity with asterisks (optional utility)
 * @param text - The text to sanitize
 * @returns Sanitized text with profanity replaced
 */
export function sanitizeProfanity(text: string): string {
  if (!text || typeof text !== 'string') {
    return text;
  }
  
  let result = text;
  
  for (const pattern of PROFANITY_PATTERNS) {
    result = result.replace(pattern, (match) => '*'.repeat(match.length));
  }
  
  return result;
}
